import { useEffect } from 'react';
import { useSocket } from '../context/SocketContext';
import { commentEventBus } from '../lib/commentEventBus';

/**
 * useCommentSync - Hook to synchronize comment state across all pages and components
 *
 * This hook listens to both Socket.IO events (for cross-tab/device sync)
 * and local event bus events (for same-page sync).
 *
 * Usage:
 * ```tsx
 * const handleCommentUpdate = (data: { commentId: number, updates: any }) => {
 *   // Update your comment state here
 *   setComments(prev => prev.map(c => c.id === data.commentId ? {...c, ...data.updates} : c));
 * };
 *
 * useCommentSync({
 *   onLike: (data) => handleCommentUpdate({ commentId: data.commentId, updates: { liked: data.liked, likes: data.likes }}),
 *   onDislike: (data) => handleCommentUpdate({ commentId: data.commentId, updates: { disliked: data.disliked, dislikes: data.dislikes }}),
 * });
 * ```
 */

interface UseCommentSyncOptions {
  /**
   * Called when a comment is liked or unliked
   */
  onLike?: (data: { commentId: number; liked: boolean; likes: number; userId?: string }) => void;

  /**
   * Called when a comment is disliked or undisliked
   */
  onDislike?: (data: { commentId: number; disliked: boolean; dislikes: number; userId?: string }) => void;

  /**
   * Called when a comment is updated
   */
  onUpdate?: (data: { commentId: number; updates: Partial<any> }) => void;

  /**
   * Called when a comment is deleted
   */
  onDelete?: (data: { commentId: number }) => void;

  /**
   * Optional: Only sync comments with these IDs (for performance optimization)
   */
  commentIds?: number[];
}

export function useCommentSync(options: UseCommentSyncOptions) {
  const { socket } = useSocket();
  const { onLike, onDislike, onUpdate, onDelete, commentIds } = options;

  useEffect(() => {
    // Helper to check if we should handle this comment
    const shouldHandle = (commentId: number) => {
      return !commentIds || commentIds.includes(commentId);
    };

    // === Socket.IO Listeners (for cross-tab/device sync) ===
    if (socket) {
      const handleSocketLike = (data: { commentId: number; liked: boolean; likes: number; userId?: string }) => {
        if (shouldHandle(data.commentId) && onLike) {
          onLike(data);
        }
      };

      const handleSocketDislike = (data: { commentId: number; disliked: boolean; dislikes: number; userId?: string }) => {
        if (shouldHandle(data.commentId) && onDislike) {
          onDislike(data);
        }
      };

      const handleSocketUpdate = (data: { commentId: number; updates: Partial<any> }) => {
        if (shouldHandle(data.commentId) && onUpdate) {
          onUpdate(data);
        }
      };

      const handleSocketDelete = (data: { commentId: number }) => {
        if (shouldHandle(data.commentId) && onDelete) {
          onDelete(data);
        }
      };

      socket.on('comment:liked', handleSocketLike);
      socket.on('comment:disliked', handleSocketDislike);
      socket.on('comment:updated', handleSocketUpdate);
      socket.on('comment:deleted', handleSocketDelete);

      return () => {
        socket.off('comment:liked', handleSocketLike);
        socket.off('comment:disliked', handleSocketDislike);
        socket.off('comment:updated', handleSocketUpdate);
        socket.off('comment:deleted', handleSocketDelete);
      };
    }
  }, [socket, onLike, onDislike, onUpdate, onDelete, commentIds]);

  useEffect(() => {
    // === Event Bus Listeners (for same-page sync) ===
    const shouldHandle = (commentId: number) => {
      return !commentIds || commentIds.includes(commentId);
    };

    const handleBusLike = (data: any) => {
      if (shouldHandle(data.commentId) && onLike) {
        onLike(data);
      }
    };

    const handleBusDislike = (data: any) => {
      if (shouldHandle(data.commentId) && onDislike) {
        onDislike(data);
      }
    };

    const handleBusUpdate = (data: any) => {
      if (shouldHandle(data.commentId) && onUpdate) {
        onUpdate(data);
      }
    };

    const handleBusDelete = (data: any) => {
      if (shouldHandle(data.commentId) && onDelete) {
        onDelete(data);
      }
    };

    const unsubLike = commentEventBus.on('comment:like', handleBusLike);
    const unsubUnlike = commentEventBus.on('comment:unlike', handleBusLike);
    const unsubDislike = commentEventBus.on('comment:dislike', handleBusDislike);
    const unsubUndislike = commentEventBus.on('comment:undislike', handleBusDislike);
    const unsubUpdate = commentEventBus.on('comment:update', handleBusUpdate);
    const unsubDelete = commentEventBus.on('comment:delete', handleBusDelete);

    return () => {
      unsubLike();
      unsubUnlike();
      unsubDislike();
      unsubUndislike();
      unsubUpdate();
      unsubDelete();
    };
  }, [onLike, onDislike, onUpdate, onDelete, commentIds]);
}
